import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  CheckboxComponent,
  RequiredInputArchConfiguration,
  RequiredInputQuadrantConfiguration,
  RequiredInputRangeConfiguration,
  RequiredInputSurfaceConfiguration,
  RequiredInputToothConfiguration,
  RequiredInputType,
  TooltipModule,
} from '@libs/ui-lib';
import { AddNewTreatmentData } from '@libs/charting';
import { filterOutIncorrectSurfaces } from '@libs/utils';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SvgIconComponent } from '@ngneat/svg-icon';

interface Tag {
  title: string;
  isSurface?: boolean;
}

@Component({
  selector: 'lib-treatment-item-pill',
  imports: [CommonModule, CheckboxComponent, TooltipModule, TranslatePipe, SvgIconComponent],
  templateUrl: './treatment-item-pill.component.html',
  styleUrl: './treatment-item-pill.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreatmentItemPillComponent {
  private readonly translateService = inject(TranslateService);

  public readonly treatment = input.required<AddNewTreatmentData>();
  public readonly checked = input(false);
  public readonly showValidationError = input(false);
  public readonly treatmentChecked = output<AddNewTreatmentData>();
  public readonly treatmentClicked = output<{ isSurface: boolean }>();

  protected readonly dentalCode = computed(() => this.treatment().dentalCode);
  protected readonly description = computed(() => this.treatment().description);
  protected readonly position = computed(() => (this.treatment().requiredInputData as RequiredInputToothConfiguration)?.position);
  protected readonly surfaces = computed(() => (this.treatment().requiredInputData as RequiredInputSurfaceConfiguration)?.surfaces);
  protected readonly range = computed(() => (this.treatment().requiredInputData as RequiredInputRangeConfiguration)?.range);
  protected readonly quadrants = computed(() => (this.treatment().requiredInputData as RequiredInputQuadrantConfiguration)?.quadrants);
  protected readonly arches = computed(() => (this.treatment().requiredInputData as RequiredInputArchConfiguration)?.arches);
  protected readonly isFullMouth = computed(() => this.treatment().requiredInputType === RequiredInputType.Mouth);
  protected readonly requiredInputTranslationString = computed(() => {
    const requiredInputType = this.treatment().requiredInputType;
    switch (requiredInputType) {
      case RequiredInputType.Tooth: {
        return 'complete_appointment_modal_select_tooth_label';
      }
      case RequiredInputType.Surface: {
        return 'complete_appointment_modal_select_surface_label';
      }
      case RequiredInputType.Range: {
        return 'complete_appointment_modal_select_range_label';
      }
      case RequiredInputType.Quadrant: {
        return 'complete_appointment_modal_select_quadrant_label';
      }
      case RequiredInputType.Arch: {
        return 'complete_appointment_modal_select_arch_label';
      }
      default: {
        return 'complete_appointment_modal_full_mouth_label';
      }
    }
  });

  public tagClass =
    'typo-h5-semibold h-[22px] rounded-[8px] bg-background-tag px-2 py-[3px] text-primary-blue hover:cursor-pointer truncate';

  public treatmentTags = computed(() => {
    const tags: Tag[] = [];

    if (this.position()) {
      tags.push({ title: this.position()!.toString() });
    }

    if (this.surfaces()) {
      const surfaces = this.surfaces()!;
      const position = this.position();

      // Filter out incorrect surfaces based on tooth position
      const filteredSurfaces = position ? filterOutIncorrectSurfaces(surfaces, position) : surfaces;

      tags.push({ title: filteredSurfaces.join(''), isSurface: true });
    }

    if (this.range()) {
      tags.push({ title: this.range()! });
    }

    if (this.quadrants()) {
      this.quadrants()!.forEach((quadrant) => {
        const translationKey = `charting_add_treatment_quadrant_${quadrant.replace('-', '_')}`;
        tags.push({ title: this.translateService.instant(translationKey) });
      });
    }

    if (this.arches()) {
      this.arches()!.forEach((arch) => {
        const translationKey = `charting_add_treatment_arch_${arch.replace('-', '_')}`;
        tags.push({ title: this.translateService.instant(translationKey) });
      });
    }

    return tags;
  });

  protected onTreatmentChecked(): void {
    this.treatmentChecked.emit(this.treatment());
  }

  protected onTreatmentClicked(isSurface?: boolean): void {
    if (!this.isFullMouth()) {
      this.treatmentClicked.emit({ isSurface: !!isSurface });
    }
  }
}
